import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Label,
  Alert
} from "reactstrap";
import Select from "react-select";
import { DonarList } from "../../api/Donar Api/DonarApi";
import { toast } from "react-toastify";

const DonarMigrationModal = ({ isOpen, toggle, sourcePersonId, sourcePersonName, onConfirm }) => {
  const [donorOptions, setDonorOptions] = useState([]);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [loading, setLoading] = useState(false);

  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (isOpen) {
      searchDonors("");
      setSelectedDonor(null);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);

  const searchDonors = async (searchText) => {
    try {
      setLoading(true);
      let searchObj = {};
      const trimmed = searchText.trim();
      if (trimmed.length > 0) {
        if (isNaN(trimmed)) {
          searchObj = { name: trimmed };
        } else {
          searchObj = { mobile: trimmed };
        }
      }

      const params = { showAll: true };
      if (Object.keys(searchObj).length > 0) {
        params.search = JSON.stringify(searchObj);
      }

      const { data } = await DonarList(params);
      if (data.status === 200) {
        const filtered = data.payload.data
          .filter((d) => Number(d.personId) !== Number(sourcePersonId))
          .map((d) => ({
            value: d.personId,
            label: `${d.name} (${d.mobile || "No Mobile"}) - ${d.village || "No Village"}`
          }));
        setDonorOptions(filtered);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error("Failed to search donor list");
    }
  };

  const handleInputChange = (inputValue, { action }) => {
    if (action === "input-change") {
      if (timer) {
        clearTimeout(timer);
      }
      const newTimer = setTimeout(() => {
        searchDonors(inputValue);
      }, 400);
      setTimer(newTimer);
    }
  };

  const handleConfirm = () => {
    if (!selectedDonor) {
      toast.warn("Please select a destination donor to migrate transactions to.");
      return;
    }

    const destinationName = selectedDonor.label.split(" (")[0];
    const isConfirmed = window.confirm(
      `Are you absolutely sure you want to migrate all transactions from "${sourcePersonName}" to "${destinationName}" and delete "${sourcePersonName}"? This action cannot be undone.`
    );

    if (isConfirmed) {
      onConfirm(selectedDonor.value);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      style={{
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)"
      }}
    >
      <ModalHeader
        toggle={toggle}
        className="bg-danger text-white border-0"
        style={{
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          fontSize: "1.25rem",
          fontWeight: "600"
        }}
      >
        ⚠️ Safe Donor Deletion & Migration
      </ModalHeader>
      <ModalBody className="p-4" style={{ backgroundColor: "#fafafa" }}>
        <Alert
          color="danger"
          style={{
            borderRadius: "6px",
            border: "none",
            backgroundColor: "#fff0f0",
            color: "#d32f2f",
            fontSize: "0.95rem",
            lineHeight: "1.5"
          }}
        >
          <strong>{sourcePersonName}</strong> has active transaction history. Deleting them directly is not allowed to prevent data loss. 
          <br /><br />
          Please select a destination donor below to securely migrate all associated transaction records before deleting this donor.
        </Alert>

        <FormGroup className="mt-4">
          <Label
            for="destinationDonor"
            style={{
              fontWeight: "600",
              color: "#333",
              marginBottom: "8px",
              display: "block"
            }}
          >
            Migrate Transactions To:
          </Label>
          <Select
            id="destinationDonor"
            options={donorOptions}
            value={selectedDonor}
            onChange={(opt) => setSelectedDonor(opt)}
            onInputChange={handleInputChange}
            isLoading={loading}
            placeholder="Search and select destination donor..."
            isSearchable
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: "6px",
                borderColor: "#ccc",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#d32f2f"
                }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#d32f2f" : state.isFocused ? "#ffebee" : "#fff",
                color: state.isSelected ? "#fff" : "#333",
                "&:active": {
                  backgroundColor: "#d32f2f"
                }
              })
            }}
          />
        </FormGroup>

        <div className="d-flex justify-content-end gap-2 mt-4 pt-2 border-top">
          <Button
            color="secondary"
            onClick={toggle}
            style={{
              borderRadius: "6px",
              fontWeight: "600",
              padding: "8px 16px"
            }}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleConfirm}
            style={{
              borderRadius: "6px",
              fontWeight: "600",
              padding: "8px 20px",
              backgroundColor: "#d32f2f",
              borderColor: "#d32f2f"
            }}
          >
            Migrate & Delete
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DonarMigrationModal;
